import dayjs from '@classic/Shared/Dayjs';

/**
 * Wall-clock format with no offset. FullCalendar is left at its default
 * `timeZone: 'local'`, so feeding it a wall-clock string pins the event to the
 * same site-local time for every viewer (reproduces the v3 behavior in timeGrid
 * week/day without a luxon/temporal connector).
 */
const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Convert a backend ISO datetime (which carries the source instant) to the
 * site-local wall-clock string used for grid positioning.
 */
export const toSiteWallClock = (isoDateTime: string, timezone: string): string =>
    dayjs(isoDateTime).tz(timezone).format(WALL_CLOCK_FORMAT);

/**
 * All-day API values are date-only (YYYY-MM-DD, no clock time). Parse as a
 * calendar day in UTC so formatting never depends on the browser timezone.
 */
const parseAllDayDate = (value: string) => dayjs.utc(value.slice(0, 10));

/**
 * Long, screen-reader-friendly date used in accessible labels.
 * All-day events pass date-only strings — display the calendar day as-is
 * with no site/browser timezone conversion.
 */
export const formatLongDate = (
    isoDateTime: string,
    timezone: string,
    allDay = false,
): string =>
    (allDay ? parseAllDayDate(isoDateTime) : dayjs(isoDateTime).tz(timezone))
        .format('dddd, MMMM Do YYYY');

/**
 * Long, screen-reader-friendly label for a calendar day cell given a plain
 * JS Date (as provided by FullCalendar's dayCellDidMount info.date).
 * Produces e.g. "Wednesday, June 10, 2026" — intentionally matches the format
 * FullCalendar's own buildDateStr uses for the cell's aria-label, so both AT
 * code paths agree on the text.
 */
export const formatCellDate = (date: Date): string =>
    dayjs(date).format('dddd, MMMM D, YYYY');

/**
 * Compact week-column header parts. FullCalendar's default week format is a
 * single "Mon 9/7" string that clips in seven narrow mobile columns (CRO-14323).
 */
export const formatWeekColumnHeader = (date: Date): { weekday: string; day: string } => ({
    weekday: dayjs(date).format('ddd'),
    day: dayjs(date).format('D'),
});

/**
 * Compact time range for tooltips: "9:00 AM - 10:00 AM (PST)" or, for all-day
 * events, a single date or date range.
 *
 * All-day end datetimes use the FullCalendar / iCal exclusive-end convention
 * (end = day after last inclusive day). Subtract one day for display so
 * multi-day tooltips match the event detail view (CRO-13779). All-day values
 * are date-only — format the calendar day as-is with no timezone conversion.
 */
export const formatTooltipTime = (
    startDateTime: string | null,
    endDateTime: string | null,
    timezone: string,
    allDay: boolean,
): string => {
    if (!startDateTime) {
        return '';
    }

    if (allDay) {
        const start = parseAllDayDate(startDateTime);
        if (endDateTime) {
            const inclusiveEnd = parseAllDayDate(endDateTime).subtract(1, 'day');
            if (inclusiveEnd.isAfter(start, 'day')) {
                return `${start.format('M/D/YYYY')} - ${inclusiveEnd.format('M/D/YYYY')}`;
            }
        }
        return start.format('M/D/YYYY');
    }

    const start = dayjs(startDateTime).tz(timezone);
    let response = start.format('h:mm A');
    if (endDateTime) {
        response += ` - ${dayjs(endDateTime).tz(timezone).format('h:mm A')}`;
    }
    response += ` (${start.format('z')})`;

    return response;
};

/**
 * "Occurs" line for list rows, mirroring the v3 list output: a single date for
 * all-day events, otherwise a start -> end range that collapses to time-only
 * when both ends fall on the same day.
 */
export const formatListOccurs = (
    startDateTime: string,
    endDateTime: string | null,
    startTime: string | null,
    endTime: string | null,
    timezone: string,
): string => {
    const start = dayjs(startDateTime).tz(timezone);

    if (!startTime) {
        return start.format('M/D/YYYY');
    }

    if (endDateTime && endTime) {
        const end = dayjs(endDateTime).tz(timezone);
        const startStr = start.format('M/D/YYYY, h:mm A');
        const endStr = end.isSame(start, 'day')
            ? end.format('h:mm A')
            : end.format('M/D/YYYY, h:mm A');
        return `${startStr} to ${endStr} (${start.format('z')})`;
    }

    return `${start.format('M/D/YYYY, h:mm A')} (${start.format('z')})`;
};
